import React, { useMemo } from "react";
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import BlockIcon from "@mui/icons-material/Block";
import DrawerHeader, { DRAWER_NO_SLIDE, DRAWER_PAPER_PROPS } from "./DrawerHeader";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SortIcon from "@mui/icons-material/Sort";

const ThemeDetailDrawer = ({
  selectedRowIndex,
  handleCloseDrawer,
  filteredThemes,
  sortDirection,
  setSortDirection,
  requestSort,
  role
}) => {
  const muiTheme = useTheme();

  // Le tri était fait dans le corps du rendu, une fois par thème du groupe, avec une
  // fonction recréée à chaque render du parent. localeCompare est lent : concentré ici,
  // il ne retombe plus dans la frame d'ouverture du Drawer, seulement à l'ouverture ou
  // quand le sens du tri change.
  const details = useMemo(() => {
    const themesGroup = filteredThemes?.[selectedRowIndex]?.themes || [];
    const direction = sortDirection === "asc" ? 1 : -1;

    return {
      title: themesGroup.map((t) => t.name).join(" · "),
      // Un groupe ne contient quasiment jamais plus d'un thème ; si ça arrive, chaque
      // version est préfixée de son nom plutôt que de n'en afficher qu'une seule.
      subtitle: `${
        themesGroup.length > 1 ? "Dernières versions" : "Dernière version"
      } : ${themesGroup
        .map((t) => {
          const version = t.sites?.[0]?.lastVersionTheme || "Non spécifiée";
          return themesGroup.length > 1 ? `${t.name} : ${version}` : version;
        })
        .join(" · ")}`,
      themes: themesGroup.map((t) => ({
        name: t.name,
        sites: [...(t.sites || [])].sort(
          (a, b) =>
            direction *
            (a.site || "")
              .toLowerCase()
              .localeCompare((b.site || "").toLowerCase())
        ),
      })),
    };
  }, [filteredThemes, selectedRowIndex, sortDirection]);

  return (
    <Drawer
      anchor="right"
      open={selectedRowIndex !== null}
      onClose={handleCloseDrawer}
      PaperProps={DRAWER_PAPER_PROPS.wide}
      SlideProps={DRAWER_NO_SLIDE}
      // Sans ça, le scroll lock de MUI pose overflow:hidden + padding-right sur
      // <body> à l'ouverture, ce qui relayoute tout le dashboard pendant l'animation.
      disableScrollLock
    >
      <div>
        {selectedRowIndex !== null && (
          <div>
            <DrawerHeader
              title={details.title}
              subtitle={details.subtitle}
              onClose={handleCloseDrawer}
              role={role}
            />
            <div className="drawerTab">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Active</TableCell>
                      <TableCell
                        align="center"
                        onClick={() => {
                          requestSort("name");
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc"
                          );
                        }}
                        style={{
                          cursor: "pointer",
                          position: "relative",
                          bottom: "4px",
                        }}
                      >
                        Site
                        <SortIcon
                          style={{
                            paddingLeft: "5px",
                            position: "relative",
                            top: "5px",
                          }}
                        />
                      </TableCell>
                      <TableCell align="left">URL</TableCell>
                      <TableCell align="center">Instance</TableCell>
                      <TableCell align="left">Version</TableCell>
                      <TableCell align="center">Mise à jour</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.themes.map((themeItem) =>
                      themeItem.sites.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: index % 2 === 0
                              ? muiTheme.palette.background.alternate
                              : muiTheme.palette.background.paper,
                          }}
                        >
                          <TableCell align="center">
                            {item.active === 1 ? (
                              <DoneIcon style={{ color: "green" }} />
                            ) : (
                              <BlockIcon style={{ color: "red" }} />
                            )}
                          </TableCell>
                          <TableCell align="center">{item.site}</TableCell>
                          <TableCell align="center">
                            <span
                              style={{
                                display: "flex",
                                cursor: "pointer",
                              }}
                            >
                              <OpenInNewIcon />
                              <a
                                href={item.url}
                                style={{
                                  paddingLeft: "5px",
                                  position: "relative",
                                  top: "3px",
                                }}
                              >
                                {item.url}
                              </a>
                            </span>
                          </TableCell>
                          <TableCell align="center">{item.env}</TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            {item.version}
                          </TableCell>
                          <TableCell align="center">
                            {item.updateMode}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ThemeDetailDrawer;
